// src/app/guards/auth.guard.ts
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { TokenService } from '../services/token.service';
import { AuthErrorService } from '../services/auth-error.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { lastValueFrom } from 'rxjs';

/**
 * Guard de autenticación unificado
 * Valida la sesión en múltiples niveles:
 * 1. Datos de usuario en localStorage
 * 2. Usuario en Firebase Auth (espera a que se restaure la sesión)
 * 3. Token de Firebase válido
 * 4. Validación con backend (cada 5 minutos) - NO bloquea si falla durante desarrollo
 */
@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  private platformId = inject(PLATFORM_ID);

  constructor(
    private router: Router,
    private tokenService: TokenService,
    private http: HttpClient,
    private authErrorService: AuthErrorService
  ) {}
  
  /**
   * Espera a que Firebase Auth restaure la sesión del usuario
   * Esto es necesario porque al recargar la página, Firebase puede tardar
   * unos milisegundos en restaurar la sesión desde el almacenamiento local
   * 
   * MEJORADO: Timeout aumentado a 30 segundos para equipos muy lentos
   * Esto da tiempo suficiente para que Firebase restaure la sesión incluso
   * en equipos con poca memoria o conexión lenta
   */
  private waitForAuthState(auth: any, timeout: number = 30000): Promise<User | null> {
    return new Promise((resolve) => {
      // Si ya hay un usuario, devolverlo inmediatamente
      if (auth.currentUser) {
        console.log('✅ [AuthGuard] Usuario ya disponible en Firebase');
        resolve(auth.currentUser);
        return;
      }

      console.log('⏳ [AuthGuard] Esperando a que Firebase restaure la sesión...');
      let resolved = false;

      // Esperar a que Firebase restaure la sesión
      const unsubscribe = onAuthStateChanged(
        auth,
        (user) => {
          if (resolved) return;
          resolved = true;
          unsubscribe();
          if (user) {
            console.log('✅ [AuthGuard] Usuario restaurado por Firebase');
          } else {
            console.warn('⚠️ [AuthGuard] Firebase no restauró usuario');
          }
          resolve(user);
        },
        (error) => {
          if (resolved) return;
          resolved = true;
          unsubscribe();
          console.error('❌ [AuthGuard] Error en onAuthStateChanged:', error);
          resolve(null);
        }
      );

      // Timeout aumentado: 30 segundos para equipos muy lentos o cuando la app vuelve de suspensión
      setTimeout(() => {
        if (resolved) return;
        resolved = true;
        unsubscribe();
        const currentUser = auth.currentUser;
        if (currentUser) {
          console.log('✅ [AuthGuard] Usuario encontrado después del timeout');
          resolve(currentUser);
        } else {
          console.warn('⚠️ [AuthGuard] Timeout esperando usuario de Firebase');
          resolve(null);
        }
      }, timeout);
    });
  }
  
  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> {

    // SSR safety: localStorage and Firebase Auth are not available on the server
    if (!isPlatformBrowser(this.platformId)) {
      return true;
    }

    // 1. Verificar si hay datos de usuario
    const userData = localStorage.getItem('data_user');
    if (!userData) {
      console.warn('⚠️ [AuthGuard] No hay datos de usuario en localStorage');
      try {
        sessionStorage.setItem('logout_reason', JSON.stringify({
          reason: 'guard_no_data_user',
          timestamp: new Date().toISOString(),
          url: state.url
        }));
      } catch (e) { /* ignore */ }
      // Guardar la URL actual para redirigir después del login
      localStorage.setItem('redirectUrl', state.url);
      this.router.navigate(['/login']);
      return false;
    }
    
    // 2. Esperar a que Firebase Auth restaure la sesión
    // Esto es crítico al recargar la página
    const auth = getAuth();
    const user = await this.waitForAuthState(auth);
    
    if (!user) {
      console.warn('⚠️ [AuthGuard] No hay usuario en Firebase Auth después de esperar');
      try {
        sessionStorage.setItem('logout_reason', JSON.stringify({
          reason: 'guard_firebase_timeout',
          timestamp: new Date().toISOString(),
          url: state.url
        }));
      } catch (e) { /* ignore */ }
      // Limpiar datos de sesión para evitar loop con login component
      localStorage.removeItem('data_user');
      localStorage.removeItem('last_auth_validation');
      // Guardar la URL actual para redirigir después del login
      localStorage.setItem('redirectUrl', state.url);
      this.router.navigate(['/login']);
      return false;
    }
    
    // 3. Verificar que el token de Firebase sea válido
    // MEJORADO: Intentar refrescar el token si está expirado, con timeout largo para equipos lentos
    try {
      // Primero intentar sin forzar refresh (más rápido)
      // MEJORADO: Agregar timeout para equipos lentos (10 segundos)
      const tokenPromise = user.getIdToken(false);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Token get timeout')), 10000)
      );
      
      await Promise.race([tokenPromise, timeoutPromise]);
      console.log('✅ [AuthGuard] Token de Firebase válido');
    } catch (error: any) {
      console.warn('⚠️ [AuthGuard] Token de Firebase puede estar expirado o timeout, intentando refrescar...');
      
      // Si falla, intentar con forceRefresh (puede ser que solo necesite refrescarse)
      // MEJORADO: Timeout más largo para refresh (15 segundos para equipos lentos)
      try {
        const refreshPromise = user.getIdToken(true);
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Token refresh timeout')), 15000)
        );
        
        await Promise.race([refreshPromise, timeoutPromise]);
        console.log('✅ [AuthGuard] Token refrescado exitosamente');
      } catch (refreshError: any) {
        console.error('❌ [AuthGuard] Token de Firebase inválido o timeout:', refreshError);
        
        // MEJORADO: Si es timeout, no cerrar sesión (puede ser equipo lento)
        if (refreshError.message?.includes('timeout') || refreshError.message === 'Token refresh timeout') {
          console.warn('⚠️ [AuthGuard] Timeout al obtener/refrescar token (posible equipo lento), permitiendo acceso');
          // Permitir continuar, el interceptor manejará los errores 401 si realmente hay problema
          return true;
        }
        
        // Solo cerrar sesión si el error es realmente de autenticación
        if (refreshError.code === 'auth/user-token-expired' || 
            refreshError.code === 'auth/invalid-user-token' ||
            refreshError.message?.includes('expired')) {
          // Mostrar error usando modal-error
          this.authErrorService.showInvalidToken(
            'Tu token de autenticación es inválido o ha expirado. Por favor, inicia sesión nuevamente.'
          );
          
          // Guardar causa del logout
          sessionStorage.setItem('logout_reason', JSON.stringify({
            reason: 'auth_guard_token_invalid',
            timestamp: new Date().toISOString(),
            url: state.url
          }));

          // Limpiar datos de sesión para evitar loop con login component
          localStorage.removeItem('data_user');
          localStorage.removeItem('last_auth_validation');
          // Guardar la URL actual para redirigir después del login
          localStorage.setItem('redirectUrl', state.url);
          this.router.navigate(['/login']);
          return false;
        } else {
          // Error de red u otro error temporal, permitir continuar
          console.warn('⚠️ [AuthGuard] Error temporal al obtener token, permitiendo acceso');
        }
      }
    }
    
    // 4. OPCIONAL: Validar con backend (solo cada 15 minutos para no sobrecargar y dar más tiempo a equipos lentos)
    // MEJORADO: Aumentado a 15 minutos para reducir carga y ser más tolerante
    // Si falla, NO bloquea el acceso (puede ser problema de red o backend)
    const lastValidation = localStorage.getItem('last_auth_validation');
    const now = Date.now();
    
    if (!lastValidation || (now - parseInt(lastValidation)) > 15 * 60 * 1000) {
      try {
        const backendToken = this.tokenService.getBackendToken();
        if (backendToken) {
          // MEJORADO: Agregar timeout para validación con backend (10 segundos)
          const validationPromise = lastValueFrom(
            this.http.post(
              `${environment.url_api}/autenticacion/is_logueado`,
              {},
              {
                headers: {
                  'authCoreKey': backendToken
                }
              }
            )
          );
          
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Validation timeout')), 10000)
          );
          
          await Promise.race([validationPromise, timeoutPromise]);
          
          localStorage.setItem('last_auth_validation', now.toString());
        }
      } catch (error: any) {
        // ⚠️ NO bloqueamos el acceso si falla la validación con backend
        // Puede ser un problema temporal de red o backend, o timeout en equipos lentos
        if (error.message?.includes('timeout') || error.message === 'Validation timeout') {
          console.warn('⚠️ [AuthGuard] Timeout en validación con backend (posible equipo lento), continuando');
        } else {
          console.warn('⚠️ [AuthGuard] Validación de sesión con backend falló (continuando):', error);
        }
        // NO redirigimos al login, permitimos continuar
      }
    }
    
    // ✅ Todo validado correctamente
    return true;
  }
}

